import React, { useContext, useEffect, useRef } from "react";
import { ModalContext } from '../../App';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';

export const Header = (props) => {
    const modalContext = useContext(ModalContext);
    const navigate = useNavigate();
    const { userId } = useAuth();

    // Refs for 5 lines
    const pathRef0 = useRef(null);
    const pathRef1 = useRef(null);
    const pathRef2 = useRef(null);
    const pathRef3 = useRef(null);
    const pathRef4 = useRef(null);
    const pathRefs = [pathRef0, pathRef1, pathRef2, pathRef3, pathRef4];

    // Refs for 4 gold pulses
    const pulseRef0 = useRef(null);
    const pulseRef1 = useRef(null);
    const pulseRef2 = useRef(null);
    const pulseRef3 = useRef(null);
    const pulseRefs = [pulseRef0, pulseRef1, pulseRef2, pulseRef3];

    const gradientRef0 = useRef(null);
    const gradientRef1 = useRef(null);
    const gradientRef2 = useRef(null);
    const gradientRef3 = useRef(null);
    const gradientRefs = [gradientRef0, gradientRef1, gradientRef2, gradientRef3];

    // Animate the wave paths and gold pulse gradients
    useEffect(() => {
        let animationId;
        const startTime = Date.now();

        // 5 lines - extra wavy, desynchronized with very different speeds
        const lineConfigs = [
            { yBase: 360, speed1: 0.7, speed2: 1.9, amp1: 110, amp2: 12, phase: 0 },
            { yBase: 365, speed1: 1.3, speed2: 0.8, amp1: 120, amp2: 14, phase: 2.7 },
            { yBase: 370, speed1: 0.5, speed2: 2.4, amp1: 130, amp2: 15, phase: 5.1 },
            { yBase: 375, speed1: 1.8, speed2: 1.1, amp1: 120, amp2: 14, phase: 1.3 },
            { yBase: 380, speed1: 1.1, speed2: 1.6, amp1: 110, amp2: 12, phase: 4.2 }
        ];

        // 4 gold pulses with same speed, different timing offsets
        const travelTime = 1400;
        const pulseConfigs = [
            { pauseTime: 2800, offset: 0, lineIndex: 2 },
            { pauseTime: 2200, offset: 900, lineIndex: 4 },
            { pauseTime: 3500, offset: 1800, lineIndex: 1 },
            { pauseTime: 2600, offset: 2700, lineIndex: 3 }
        ];

        // Track when to reassign pulses to new lines
        const lastCycleCount = [0, 0, 0, 0];

        const animate = () => {
            const elapsed = (Date.now() - startTime) / 1000;
            const elapsedMs = Date.now() - startTime;

            // Store current path data for each line
            const pathData = [];

            // Update each of the 5 lines - asymmetric left/right
            for (let i = 0; i < 5; i++) {
                const config = lineConfigs[i];
                const phase = config.phase;
                const yBase = config.yBase + Math.sin(elapsed * config.speed1 + phase) * config.amp2 + Math.sin(elapsed * config.speed2 + phase * 0.7) * (config.amp2 * 0.6);
                const yCtrl1 = yBase + Math.sin(elapsed * config.speed1 * 1.3 + phase) * (config.amp1 * 0.25) + Math.cos(elapsed * config.speed2 * 1.2 + phase) * (config.amp1 * 0.12);
                const yCtrl2 = yBase + Math.sin(elapsed * config.speed1 * 0.9 + 1.5 + phase) * (config.amp1 * 0.4) + Math.cos(elapsed * config.speed2 * 0.8 + phase) * (config.amp1 * 0.2);
                const pathD = `M0,${yBase} Q360,${yCtrl1} 720,${yCtrl2} T1440,${yBase}`;

                pathData[i] = pathD;
                if (pathRefs[i].current) pathRefs[i].current.setAttribute('d', pathD);
            }

            // Update each of the 4 gold pulses
            for (let i = 0; i < 4; i++) {
                const config = pulseConfigs[i];
                const cycleTotal = travelTime + config.pauseTime;
                const staggeredMs = (elapsedMs + config.offset) % cycleTotal;
                const cycleCount = Math.floor((elapsedMs + config.offset) / cycleTotal);

                // Reassign to random line at start of each new cycle
                if (cycleCount > lastCycleCount[i]) {
                    lastCycleCount[i] = cycleCount;
                    pulseConfigs[i].lineIndex = Math.floor(Math.random() * 5);
                }

                // Update pulse path to follow its assigned line
                if (pulseRefs[i].current) {
                    pulseRefs[i].current.setAttribute('d', pathData[config.lineIndex]);
                }

                // Animate gold gradient - all same speed
                let pulsePos;
                if (staggeredMs < travelTime) {
                    pulsePos = -15 + (staggeredMs / travelTime) * 130;
                } else {
                    pulsePos = 120;
                }

                if (gradientRefs[i].current) {
                    const stops = gradientRefs[i].current.children;
                    stops[0].setAttribute('offset', `${Math.max(0, pulsePos - 15)}%`);
                    stops[1].setAttribute('offset', `${Math.max(0, pulsePos - 7)}%`);
                    stops[2].setAttribute('offset', `${pulsePos}%`);
                    stops[3].setAttribute('offset', `${Math.min(100, pulsePos + 2)}%`);
                }
            }

            animationId = requestAnimationFrame(animate);
        };

        animate();
        return () => cancelAnimationFrame(animationId);
    }, []);

    const handleButtonClick = (link) => {
        window.location.href = link;
    };

    return (
        <header id="header">
            <div className="intro sine-background">
                {/* Electric Whip SVG */}
                <svg
                    className="electric-whip"
                    viewBox="0 0 1440 500"
                    preserveAspectRatio="none"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                        zIndex: 1,
                        willChange: 'contents',
                        transform: 'translateZ(0)'
                    }}
                >
                    <defs>
                        <filter id="glow">
                            <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
                            <feMerge>
                                <feMergeNode in="coloredBlur"/>
                                <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                        </filter>
                        <filter id="goldGlow">
                            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                            <feMerge>
                                <feMergeNode in="coloredBlur"/>
                                <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                        </filter>
                        {/* 4 gold gradients */}
                        {[0, 1, 2, 3].map(i => (
                            <linearGradient key={i} ref={gradientRefs[i]} id={`goldGradient${i}`} x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="rgba(251, 191, 36, 0)" />
                                <stop offset="8%" stopColor="rgba(251, 191, 36, 0.5)" />
                                <stop offset="15%" stopColor="rgba(255, 220, 80, 1)" />
                                <stop offset="17%" stopColor="rgba(251, 191, 36, 0)" />
                            </linearGradient>
                        ))}
                    </defs>

                    {/* 5 blue lines */}
                    {[0, 1, 2, 3, 4].map(i => (
                        <path
                            key={i}
                            ref={pathRefs[i]}
                            d={`M0,${330 + i * 20} Q360,${310 + i * 20} 720,${350 + i * 20} T1440,${330 + i * 20}`}
                            fill="none"
                            stroke="rgba(56, 189, 248, 0.5)"
                            strokeWidth="0.4"
                            filter="url(#glow)"
                        />
                    ))}

                    {/* 4 gold pulses */}
                    {[0, 1, 2, 3].map(i => (
                        <path
                            key={i}
                            ref={pulseRefs[i]}
                            d="M0,360 Q360,325 720,395 T1440,360"
                            fill="none"
                            stroke={`url(#goldGradient${i})`}
                            strokeWidth="2.5"
                            filter="url(#goldGlow)"
                        />
                    ))}
                </svg>
                <div className="overlay sine-background">
                    <div className="container wide-width">
                        <div className="row">
                            <div className="intro-text">
                                <h1 className="responsive-title">
                                    {props.titleOverride ? props.titleOverride : props.data ? props.data.title : "Loading"}
                                </h1>
                                <p>{props.paragraphOverride ? props.paragraphOverride : props.data ? props.data.paragraph : "Loading"}</p>
                                <div className="intro-buttons">
                                    <button
                                        className="btn-square btn-primary btn-very-wide btn-lg btn-bold"
                                        onClick={() => handleButtonClick(props.ctaLink || '/book-demo')}
                                    >
                                        {props.ctaText || "Book Demo"}
                                    </button>
                                    <button
                                        className="btn-square btn-white-outline btn-very-wide btn-lg btn-bold"
                                        onClick={() => handleButtonClick('#about')}
                                    >
                                        Learn More
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
};
